import React, { useState, useEffect, useRef, useCallback, useContext, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useNotifications } from "../context/NotificationContext";
import { SettingsContext } from "../context/SettingsContext";
import { useTasks } from "../context/TaskContext";
import api from "../api/axiosConfig";

import leaveIcon from "../assets/icons/leave.svg";
import searchIcon from "../assets/icons/search.svg";
import notificationIcon from "../assets/icons/notification.svg";
import userIcon from "../assets/icons/user.svg";
import profilePhoto from "../assets/images/profile.jpg";

import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";

import {
  SunIcon,
  MoonIcon,
  ArrowRightOnRectangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import Logo from "./common/Logo";

// ─────────────────────────────────────────────────────────────────────────────
// SEARCHABLE PAGES
// Every path here is verified against App.js — no invented routes.
// ─────────────────────────────────────────────────────────────────────────────
const SEARCHABLE_PAGES = [
  // ── Core ──
  { label: "Dashboard",             path: "/admin/dashboard",                   category: "Core",             keywords: ["home", "overview", "summary", "main"] },
  { label: "Notifications",         path: "/notifications",                     category: "Core",             keywords: ["alerts", "messages", "updates"] },
  { label: "Settings",              path: "/admin/settings",                    category: "Core",             keywords: ["preferences", "config", "account"] },
  { label: "Communication Panel",   path: "/admin/communication",               category: "Core",             keywords: ["chat", "messages", "communication", "panel"] },

  // ── Projects & Tasks ──
  { label: "Projects",              path: "/admin/projects",                    category: "Projects & Tasks", keywords: ["projects", "construction", "sites", "jobs", "all projects"] },
  { label: "Tasks",                 path: "/admin/Task",                        category: "Projects & Tasks", keywords: ["tasks", "todo", "work items", "assignments", "all tasks", "daily log", "task logs"] },
  { label: "Add Task",              path: "/admin/Task/add",                    category: "Projects & Tasks", keywords: ["new task", "create task", "add task"] },

  // ── Site Operations & DPRs (Customer & Site Role) ──
  { label: "Daily Progress Reports (DPR)", path: "/admin/customer-portal?tab=dpr", category: "Site Operations", keywords: ["daily log", "dpr", "site log", "daily report", "shift log", "work done", "progress log", "site progress", "daily progress", "filed dpr"] },
  { label: "File Today's DPR", path: "/admin/customer-portal?tab=dpr", category: "Site Operations", keywords: ["file dpr", "add dpr", "new daily log", "create dpr", "log progress", "submit dpr", "daily log form"] },
  { label: "GPS Site Attendance", path: "/admin/customer-portal?tab=attendance", category: "Site Operations", keywords: ["attendance", "gps", "clock in", "clock out", "muster roll", "labor attendance", "geofence", "site attendance", "check in"] },
  { label: "Material Indents (Requisitions)", path: "/admin/customer-portal?tab=indents", category: "Site Operations", keywords: ["material request", "indent", "site indent", "requisition", "material order", "request materials"] },
  { label: "Gate GRN (Delivery Receiving)", path: "/admin/customer-portal?tab=indents", category: "Site Operations", keywords: ["grn", "gate grn", "delivery", "gate receiving", "challan", "truck delivery", "material received", "receive gate grn"] },
  { label: "Approvals on the Go", path: "/admin/customer-portal?tab=approvals", category: "Site Operations", keywords: ["approvals", "po approval", "approve po", "bill approval", "indent approval", "pending approvals", "po approvals"] },
  { label: "Quality & Safety Inspections", path: "/admin/customer-portal?tab=inspections", category: "Site Operations", keywords: ["inspections", "safety", "hse", "audit", "checklist", "quality checklist", "site audit", "safety inspection"] },
  { label: "Site Snag List & Defects", path: "/admin/customer-portal?tab=inspections", category: "Site Operations", keywords: ["snag", "snags", "punch list", "defects", "report snag", "rectification", "snag list"] },
  { label: "Site Blueprints & Drawings", path: "/admin/customer-portal?tab=documents", category: "Site Operations", keywords: ["blueprints", "drawings", "plans", "architectural", "structural drawings", "site plans", "specifications", "site documents"] },
  { label: "Site Operations Console", path: "/admin/customer-portal?tab=overview", category: "Site Operations", keywords: ["customer portal", "client portal", "site console", "site operations", "site overview"] },

  // ── Inventory ──
  { label: "Inventory",             path: "/admin/inventory",                   category: "Inventory",        keywords: ["inventory", "stock", "items", "materials list"] },
  { label: "Add Inventory Item",    path: "/admin/inventory/add",               category: "Inventory",        keywords: ["add inventory", "new item", "create inventory"] },
  { label: "Material Issue",        path: "/admin/inventory/issue",             category: "Inventory",        keywords: ["material issue", "issue material", "dispatch"] },
  { label: "Warehouse Management",  path: "/admin/warehouse-management",        category: "Inventory",        keywords: ["warehouse", "storage", "wms", "store"] },
  { label: "Internal Picking",      path: "/admin/material/internal-picking",   category: "Inventory",        keywords: ["picking", "internal pick", "transfer", "material picking"] },

  // ── Purchase & Material ──
  { label: "Purchase Requests",     path: "/admin/material/purchase",           category: "Purchase",         keywords: ["purchase", "purchase request", "pr", "buy"] },
  { label: "New Purchase Request",  path: "/admin/material/purchase/new",       category: "Purchase",         keywords: ["create purchase", "new pr", "add purchase", "raise request"] },
  { label: "Purchase Orders",       path: "/admin/material/purchase-orders",    category: "Purchase",         keywords: ["purchase order", "po", "order list", "orders"] },
  { label: "Scrap",                 path: "/admin/material/scrap",              category: "Purchase",         keywords: ["scrap", "waste", "discard", "disposal"] },
  { label: "Scrap Reuse",           path: "/admin/material/scrap/reuse",        category: "Purchase",         keywords: ["reuse scrap", "recycle", "scrap reuse"] },
  { label: "Scrap Sale",            path: "/admin/material/scrap/sell",         category: "Purchase",         keywords: ["sell scrap", "scrap sale", "scrap disposal"] },
  { label: "Receipt",               path: "/admin/material/receipts/view",      category: "Purchase",         keywords: ["receipt", "goods receipt", "grn", "receive"] },

  // ── Finance & Accounting ──
  { label: "Accounting Dashboard",  path: "/admin/accounting/dashboard",        category: "Finance",          keywords: ["accounting", "finance", "financial dashboard", "accounts"] },
  { label: "Budget",                path: "/admin/BudgetPage",                  category: "Finance",          keywords: ["budget", "budgeting", "cost plan", "financial plan"] },
  { label: "Add Budget",            path: "/admin/BudgetPage/add",              category: "Finance",          keywords: ["new budget", "create budget", "add budget"] },
  { label: "Budget vs Actual",      path: "/admin/accounting/reports/budget",   category: "Finance",          keywords: ["budget vs actual", "variance", "budget report", "cost comparison"] },
  { label: "Invoices",              path: "/admin/accounting/invoices",         category: "Finance",          keywords: ["invoice", "invoices", "billing", "receivables"] },
  { label: "New Invoice",           path: "/admin/accounting/invoices/new",     category: "Finance",          keywords: ["create invoice", "add invoice", "new invoice", "raise invoice"] },
  { label: "Vendor Bills",          path: "/admin/accounting/bills",            category: "Finance",          keywords: ["vendor bill", "bills", "payables", "supplier bill"] },
  { label: "New Vendor Bill",       path: "/admin/accounting/bills/new",        category: "Finance",          keywords: ["add bill", "create bill", "new vendor bill"] },
  { label: "General Ledger",        path: "/admin/accounting/ledger",           category: "Finance",          keywords: ["ledger", "general ledger", "gl", "accounts ledger", "journal"] },

  // ── Assets ──
  { label: "Assets",                path: "/admin/assets",                      category: "Assets",           keywords: ["assets", "equipment", "machinery", "tools", "fixed assets"] },
  { label: "Add Asset",             path: "/admin/assets/add",                  category: "Assets",           keywords: ["new asset", "add equipment", "add machinery", "create asset"] },

  // ── Employee & Workforce ──
  { label: "Employees",             path: "/admin/EmployeePage",                category: "HR",               keywords: ["employees", "staff", "people", "hr", "human resource", "workers"] },
  { label: "Add Employee",          path: "/admin/EmployeePage/add",            category: "HR",               keywords: ["new employee", "add staff", "hire", "onboard"] },
  { label: "Workforce",             path: "/admin/workforce",                   category: "HR",               keywords: ["workforce", "labour", "labor", "manpower", "site workers"] },
  { label: "Add Workforce",         path: "/admin/workforce/add",               category: "HR",               keywords: ["add workforce", "new labour", "new worker", "add manpower"] },

  // ── Vendors ──
  { label: "Vendors",               path: "/admin/VendorPage",                  category: "Vendors",          keywords: ["vendor", "vendors", "suppliers", "contractor list"] },
  { label: "Add Vendor",            path: "/admin/VendorPage/add",              category: "Vendors",          keywords: ["new vendor", "add supplier", "create vendor"] },

  // ── Procurement ──
  { label: "Procurement",           path: "/admin/procurement",                 category: "Procurement",      keywords: ["procurement", "sourcing", "buying", "supply chain"] },
  { label: "Add Procurement",       path: "/admin/procurement/add",             category: "Procurement",      keywords: ["new procurement", "create procurement", "add sourcing"] },

  // ── Documents & Compliance ──
  { label: "Documents",             path: "/admin/DocumentPage",                category: "Compliance",       keywords: ["documents", "files", "docs", "document management"] },
  { label: "Add Document",          path: "/admin/DocumentPage/add",            category: "Compliance",       keywords: ["upload document", "new document", "add file"] },
  { label: "Compliance & Safety",   path: "/admin/compliance-safety",           category: "Compliance",       keywords: ["compliance", "safety", "hse", "regulation", "audit", "risk"] },
  { label: "Add Compliance",        path: "/admin/compliance-safety/add",       category: "Compliance",       keywords: ["new compliance", "add safety", "create compliance"] },

  // ── Reports ──
  { label: "Reporting & Analytics", path: "/admin/Reporting-Analytics",         category: "Reports",          keywords: ["reports", "analytics", "reporting", "statistics", "charts", "data"] },

  // ── CRM ──
  { label: "CRM",                   path: "/crm",                               category: "CRM",              keywords: ["crm", "clients", "leads", "customer", "relationship"] },

  // ── User & Role Management ──
  { label: "Users",                 path: "/admin/users",                       category: "Admin",            keywords: ["users", "user list", "team members", "user management"] },
  { label: "Add User",              path: "/admin/users/add",                   category: "Admin",            keywords: ["new user", "create user", "invite user", "add member"] },
  { label: "Roles",                 path: "/admin/roles",                       category: "Admin",            keywords: ["roles", "role management", "permissions", "access control"] },
  { label: "Add Role",              path: "/admin/roles/add",                   category: "Admin",            keywords: ["new role", "create role", "add role"] },
  { label: "Subscriptions & Trials", path: "/admin/subscriptions",              category: "Admin",            keywords: ["subscription", "trial", "billing", "plans", "pricing", "saas", "payment"] },
];

// Quick links shown in idle state — all verified real routes
const QUICK_LINKS = [
  { label: "Daily Progress Reports (DPR)", path: "/admin/customer-portal?tab=dpr",      category: "Site Operations" },
  { label: "Task Daily Logs (Timesheets)", path: "/admin/Task?tab=timesheets",          category: "Projects & Tasks" },
  { label: "Site Operations Console",     path: "/admin/customer-portal?tab=overview", category: "Site Operations" },
  { label: "Projects",                    path: "/admin/projects",                      category: "Projects & Tasks" },
  { label: "Tasks",                       path: "/admin/Task",                          category: "Projects & Tasks" },
  { label: "Accounting Dashboard",        path: "/admin/accounting/dashboard",         category: "Finance" },
  { label: "Inventory",                   path: "/admin/inventory",                     category: "Inventory" },
];

const RECENT_KEY = "cms_recent_searches";
const MAX_RECENT  = 5;

// ─────────────────────────────────────────────────────────────────────────────
// Highlight — bolds the matched portion of a result label
// ─────────────────────────────────────────────────────────────────────────────
const Highlight = ({ text, query }) => {
  if (!query.trim()) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-[#FFE8DC] text-[#E8612C] rounded px-0.5 not-italic font-semibold">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// localStorage helpers
// ─────────────────────────────────────────────────────────────────────────────
const getRecent = () => {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); }
  catch { return []; }
};
const saveRecent = (item) => {
  const prev = getRecent().filter((r) => r.path !== item.path);
  localStorage.setItem(RECENT_KEY, JSON.stringify([item, ...prev].slice(0, MAX_RECENT)));
};
const clearRecent = () => localStorage.removeItem(RECENT_KEY);

// ─────────────────────────────────────────────────────────────────────────────
// Keyboard hint row — reused in both idle and results states
// ─────────────────────────────────────────────────────────────────────────────
const KeyboardHints = () => (
  <div className="px-4 py-2 border-t border-slate-100 dark:border-[#30363d] bg-slate-50/80 dark:bg-[#21262d] flex items-center gap-3 text-[10px] text-slate-400 dark:text-[#848d97] select-none">
    <span><kbd className="font-mono bg-white dark:bg-[#161b22] border border-slate-200 dark:border-[#30363d] px-1 py-0.5 rounded text-[9px] text-slate-600 dark:text-slate-300">↑↓</kbd> navigate</span>
    <span><kbd className="font-mono bg-white dark:bg-[#161b22] border border-slate-200 dark:border-[#30363d] px-1 py-0.5 rounded text-[9px] text-slate-600 dark:text-slate-300">↵</kbd> open</span>
    <span><kbd className="font-mono bg-white dark:bg-[#161b22] border border-slate-200 dark:border-[#30363d] px-1 py-0.5 rounded text-[9px] text-slate-600 dark:text-slate-300">ESC</kbd> close</span>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// TOPBAR
// ─────────────────────────────────────────────────────────────────────────────
const Topbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { notifications, unreadCount, markRead, markAllRead, fetchNotifications } = useNotifications();
  const { theme, saveSettings } = useContext(SettingsContext);
  const taskData = useTasks();
  const safeTasks = Array.isArray(taskData?.tasks) ? taskData.tasks : [];

  const [showNotifications, setShowNotifications] = useState(false);
  const [showSearch,        setShowSearch]         = useState(false);
  const [showProfile,       setShowProfile]        = useState(false);
  const [showProfilePopup,  setShowProfilePopup]   = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showMobileHub,     setShowMobileHub]     = useState(false);
  const [menuCoords,        setMenuCoords]         = useState({ top: 0, right: 0 });

  // ── close logout confirmation on escape key ──
  useEffect(() => {
    if (!showLogoutConfirm) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setShowLogoutConfirm(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showLogoutConfirm]);

  // ── Global shortcut: Ctrl+K / Cmd+K to toggle Search & Escape to close ──
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setShowSearch((prev) => !prev);
        setShowNotifications(false);
        setShowProfile(false);
      }
      if (e.key === "Escape" && showSearch) {
        setShowSearch(false);
      }
    };
    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [showSearch]);

  // Dynamic searchable pages combining static definitions and tasks
  const dynamicPages = useMemo(() => {
    const list = [...SEARCHABLE_PAGES];
    const firstTask = safeTasks.length > 0 ? safeTasks[0] : null;

    // Direct entry for Daily Logs under Task Details
    list.unshift({
      label: "Task Daily Logs (Detail View)",
      path: firstTask ? `/admin/task/view/${firstTask.id}?tab=timesheets` : "/admin/Task?tab=timesheets",
      category: "Projects & Tasks",
      keywords: ["daily log", "daily logs", "task daily log", "task detail daily log", "task logs", "timesheet", "timesheets", "log hours", "daily log view"]
    });

    // Also include specific tasks so user can jump straight into any task's daily log
    safeTasks.slice(0, 15).forEach((t) => {
      list.push({
        label: `${t.title} — Daily Logs`,
        path: `/admin/task/view/${t.id}?tab=timesheets`,
        category: "Task Daily Logs",
        keywords: ["daily log", "daily logs", "task log", "timesheet", t.title.toLowerCase()]
      });
    });

    return list;
  }, [safeTasks]);

  const [searchQuery,     setSearchQuery]     = useState("");
  const [searchResults,   setSearchResults]   = useState([]);
  const [activeIndex,     setActiveIndex]     = useState(-1);
  const [recentSearches,  setRecentSearches]  = useState([]);
  const debounceRef        = useRef(null);
  const searchContainerRef = useRef(null);
  const searchInputRef     = useRef(null);
  const searchListRef      = useRef(null);
  const mobileSearchRef    = useRef(null);
  const mobileInputRef     = useRef(null);
  const searchButtonRef    = useRef(null);

  const [searchCoords, setSearchCoords] = useState({ top: 68, left: 240, width: 440 });

  const updateSearchCoords = useCallback(() => {
    if (searchContainerRef.current) {
      const rect = searchContainerRef.current.getBoundingClientRect();
      setSearchCoords({
        top: Math.round(rect.bottom + 6),
        left: Math.round(rect.left),
        width: Math.round(rect.width),
      });
    }
  }, []);

  useEffect(() => {
    if (showSearch) {
      updateSearchCoords();
      window.addEventListener("resize", updateSearchCoords);
      window.addEventListener("scroll", updateSearchCoords, true);
      return () => {
        window.removeEventListener("resize", updateSearchCoords);
        window.removeEventListener("scroll", updateSearchCoords, true);
      };
    }
  }, [showSearch, updateSearchCoords]);

  const notificationRef       = useRef(null);
  const notificationButtonRef = useRef(null);
  const profileRef            = useRef(null);
  const profileButtonRef      = useRef(null);
  const mobileHubRef          = useRef(null);
  const mobileHubButtonRef    = useRef(null);

  // ── close on outside click / scroll / resize ──
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notificationRef.current && !notificationRef.current.contains(e.target) && !notificationButtonRef.current?.contains(e.target))
        setShowNotifications(false);
      if (profileRef.current && !profileRef.current.contains(e.target) && !profileButtonRef.current?.contains(e.target))
        setShowProfile(false);
      if (mobileHubRef.current && !mobileHubRef.current.contains(e.target) && !mobileHubButtonRef.current?.contains(e.target))
        setShowMobileHub(false);
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target) &&
        (!searchListRef.current || !searchListRef.current.contains(e.target)) &&
        (!mobileSearchRef.current || !mobileSearchRef.current.contains(e.target)) &&
        (!searchButtonRef.current || !searchButtonRef.current?.contains(e.target))
      ) {
        setShowSearch(false);
      }
    };
    const handleScroll = (e) => {
      const target = e.target instanceof Node ? e.target : null;
      const inside = target && (
        (notificationRef.current && notificationRef.current.contains(target)) ||
        (profileRef.current      && profileRef.current.contains(target))      ||
        (mobileHubRef.current    && mobileHubRef.current.contains(target))    ||
        (searchContainerRef.current && searchContainerRef.current.contains(target)) ||
        (searchListRef.current   && searchListRef.current.contains(target))   ||
        (mobileSearchRef.current && mobileSearchRef.current.contains(target))
      );
      if (inside) return;
      setShowNotifications(false);
      setShowProfile(false);
      setShowSearch(false);
      setShowMobileHub(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleScroll);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  // ── search filter ──
  const runSearch = useCallback(async (q) => {
    const trimmed = q.trim();
    if (!trimmed) { setSearchResults([]); setActiveIndex(-1); return; }
    const lower = trimmed.toLowerCase();

    // 1. Immediate local matching for zero-latency response
    const localMatches = dynamicPages.filter(
      (p) =>
        p.label.toLowerCase().includes(lower) ||
        p.category.toLowerCase().includes(lower) ||
        p.keywords.some((kw) => kw.toLowerCase().includes(lower))
    );

    // 2. Query centralized backend Global Search API
    try {
      const res = await api.get(`/search?q=${encodeURIComponent(trimmed)}`);
      if (res.data?.items && Array.isArray(res.data.items)) {
        const remoteItems = res.data.items.map(it => ({
          label: it.title,
          path: it.url,
          category: it.category || "Search Results",
          subtitle: it.subtitle
        }));

        const seen = new Set();
        const merged = [];
        for (const item of [...remoteItems, ...localMatches]) {
          if (!seen.has(item.path)) {
            seen.add(item.path);
            merged.push(item);
          }
        }
        setSearchResults(merged);
        setActiveIndex(-1);
        return;
      }
    } catch {
      // Graceful fallback to client navigation index if backend is unreachable
    }

    setSearchResults(localMatches);
    setActiveIndex(-1);
  }, [dynamicPages]);

  const handleQueryChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(val), 180);
  };

  // autofocus + load recents when opened; reset on close
  useEffect(() => {
    if (showSearch) {
      setRecentSearches(getRecent());
      setTimeout(() => {
        if (window.innerWidth >= 1024) {
          searchInputRef.current?.focus();
        } else {
          mobileInputRef.current?.focus();
        }
      }, 60);
    } else {
      clearTimeout(debounceRef.current);
      setSearchQuery("");
      setSearchResults([]);
      setActiveIndex(-1);
    }
  }, [showSearch]);

  // scroll active item into view
  useEffect(() => {
    if (activeIndex >= 0 && searchListRef.current) {
      const items = searchListRef.current.querySelectorAll("[data-result-item]");
      items[activeIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  const handleSearchKeyDown = (e) => {
    const total = searchResults.length;
    if (!total && e.key !== "Escape") return;
    if (e.key === "ArrowDown")  { e.preventDefault(); setActiveIndex((p) => (p + 1) % total); }
    else if (e.key === "ArrowUp")   { e.preventDefault(); setActiveIndex((p) => (p <= 0 ? total - 1 : p - 1)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      const target = activeIndex >= 0 ? searchResults[activeIndex] : total >= 1 ? searchResults[0] : null;
      if (target) handleNavigate(target);
    } else if (e.key === "Escape") {
      setShowSearch(false);
    }
  };

  const handleNavigate = (item) => {
    saveRecent({ label: item.label, path: item.path, category: item.category });
    setRecentSearches(getRecent());
    navigate(item.path);
    setShowSearch(false);
  };

  const handleClearRecent = (e) => {
    e.stopPropagation();
    clearRecent();
    setRecentSearches([]);
  };

  const handleClearQuery = () => {
    setSearchQuery("");
    setSearchResults([]);
    setActiveIndex(-1);
    if (window.innerWidth >= 1024) {
      searchInputRef.current?.focus();
    } else {
      mobileInputRef.current?.focus();
    }
  };

  // group results by category
  const groupedResults = searchResults.reduce((acc, item, idx) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push({ ...item, globalIdx: idx });
    return acc;
  }, {});

  const renderSearchContent = () => (
    <div className="py-2">
      {/* Results state */}
      {searchQuery.trim() !== "" && (
        searchResults.length > 0 ? (
          <>
            {Object.entries(groupedResults).map(([category, items]) => (
              <div key={category}>
                <p className="px-4 pt-2.5 pb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-[#848d97] select-none">
                  {category}
                </p>
                {items.map((item) => (
                  <button
                    key={`${item.category}-${item.path}-${item.label}`}
                    data-result-item
                    onClick={() => handleNavigate(item)}
                    className={`w-full text-left flex items-center gap-3 px-4 py-2.5 transition-colors ${
                      activeIndex === item.globalIdx 
                        ? "bg-orange-50/80 dark:bg-orange-950/40 text-[#D9531E] dark:text-orange-300 font-semibold" 
                        : "hover:bg-slate-50 dark:hover:bg-[#21262d] text-slate-800 dark:text-slate-200"
                    }`}
                  >
                    <span className="flex-1 text-sm font-medium leading-snug">
                      <Highlight text={item.label} query={searchQuery} />
                    </span>
                    <svg className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ))}
              </div>
            ))}
            <KeyboardHints />
          </>
        ) : (
          <div className="px-4 py-8 text-center flex flex-col items-center gap-1.5">
            <svg className="w-8 h-8 text-slate-300 dark:text-slate-600 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
            <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">No results found</p>
            <p className="text-xs text-slate-400">
              Nothing matched "<span className="font-medium text-slate-600 dark:text-slate-300">{searchQuery}</span>"
            </p>
          </div>
        )
      )}

      {/* Idle state */}
      {searchQuery.trim() === "" && (
        <>
          {recentSearches.length > 0 && (
            <div>
              <div className="flex items-center justify-between px-4 pt-2 pb-1">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-[#848d97] select-none">Recent</p>
                <button onClick={handleClearRecent} className="text-[10px] text-slate-400 hover:text-red-500 transition font-medium">
                  Clear
                </button>
              </div>
              {recentSearches.map((item) => (
                <button
                  key={`recent-${item.path}`}
                  onClick={() => handleNavigate(item)}
                  className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-slate-50 dark:hover:bg-[#21262d] text-slate-700 dark:text-slate-200 transition"
                >
                  <svg className="w-3.5 h-3.5 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="flex-1 text-sm font-medium">{item.label}</span>
                  <span className="text-[10px] text-slate-400 dark:text-slate-400 bg-slate-100 dark:bg-[#30363d] px-2 py-0.5 rounded-full shrink-0">
                    {item.category}
                  </span>
                </button>
              ))}
            </div>
          )}

          <div>
            <p className="px-4 pt-2.5 pb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-[#848d97] select-none">
              Quick Links
            </p>
            {QUICK_LINKS.map((item) => (
              <button
                key={`quick-${item.path}`}
                onClick={() => handleNavigate({ ...item, keywords: [] })}
                className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-slate-50 dark:hover:bg-[#21262d] text-slate-700 dark:text-slate-200 transition"
              >
                <span className="flex-1 text-sm font-medium">{item.label}</span>
                <svg className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ))}
          </div>

          <KeyboardHints />
        </>
      )}
    </div>
  );

  // ── toggle helpers ──
  const calcCoords = (ref, popupWidth = 320) => {
    if (!ref.current) return { top: 68, right: 12 };
    const rect = ref.current.getBoundingClientRect();
    const top = rect.bottom + 8;
    // On mobile, clamp so popup doesn't overflow left edge of screen
    const desiredRight = window.innerWidth - rect.right;
    const maxRight = window.innerWidth - Math.min(popupWidth, window.innerWidth - 16) - 8;
    const right = Math.min(desiredRight, maxRight);
    return { top, right: Math.max(right, 8) };
  };
  const toggleNotifications = () => {
    if (!showNotifications) {
      setMenuCoords(calcCoords(notificationButtonRef));
      if (typeof fetchNotifications === 'function') {
        fetchNotifications(true);
      }
    }
    setShowNotifications((v) => !v);
    setShowSearch(false);
    setShowProfile(false);
    setShowMobileHub(false);
  };
  const toggleSearch = () => {
    setShowSearch((v) => !v);
    setShowNotifications(false);
    setShowProfile(false);
    setShowMobileHub(false);
  };
  const toggleProfile = () => {
    if (!showProfile) setMenuCoords(calcCoords(profileButtonRef));
    setShowProfile((v) => !v);
    setShowNotifications(false);
    setShowSearch(false);
    setShowMobileHub(false);
  };
  const toggleMobileHub = () => {
    if (!showMobileHub) setMenuCoords(calcCoords(mobileHubButtonRef, 310));
    setShowMobileHub((v) => !v);
    setShowNotifications(false);
    setShowSearch(false);
    setShowProfile(false);
  };

  const handleLogout = () => {
    document.documentElement.classList.remove('dark');
    logout();
    navigate("/login");
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "success": return <CheckCircleIcon         className="w-5 h-5 text-emerald-500" />;
      case "warning": return <ExclamationTriangleIcon  className="w-5 h-5 text-amber-500"  />;
      case "error":   return <XCircleIcon              className="w-5 h-5 text-red-500"    />;
      default:        return <InformationCircleIcon    className="w-5 h-5 text-blue-500"   />;
    }
  };

  const getUserHandle = () => {
    if (user?.username) return `@${user.username}`;
    if (user?.email) {
      const handle = user.email.split('@')[0];
      return `@${handle}`;
    }
    if (user?.name) {
      return `@${user.name.toLowerCase().replace(/[^a-z0-9]/g, '')}`;
    }
    return "@user";
  };

  const getJoinedDate = () => {
    const rawDate = user?.created_at || user?.createdAt || user?.joined_at || user?.joinedDate;
    if (rawDate) {
      const d = new Date(rawDate);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      }
    }
    if (user?.iat) {
      const d = new Date(user.iat * 1000);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      }
    }
    return new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getLastLoginDisplay = () => {
    if (user?.last_login || user?.lastLogin) {
      const d = new Date(user.last_login || user.lastLogin);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
      }
    }
    const sessionTime = localStorage.getItem('briq_last_login');
    if (sessionTime) {
      const d = new Date(sessionTime);
      if (!isNaN(d.getTime())) {
        return 'Today at ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      }
    }
    return "Active session";
  };

  const getUserInitials = () => {
    if (!user?.name) return (user?.email ? user.email.charAt(0).toUpperCase() : "U");
    const parts = user.name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return user.name.slice(0, 2).toUpperCase();
  };

  const handleNotificationClick = (note) => {
    if (!note.is_read) markRead(note.id);
    setShowNotifications(false);
    navigate("/notifications");
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="flex justify-between items-center w-full h-full relative z-50 min-w-0">

      {/* Brand */}
      <div className="flex items-center pr-2 min-w-0 shrink-0">
        <a href="/admin/dashboard" aria-label="BriqERP home" className="flex items-center shrink-0">
          <Logo className="h-6 sm:h-7 w-auto shrink-0" />
        </a>
      </div>

      {/* Search Bar with attached Dropdown Sheet (Inline in Topbar, No Screen-Dimming Backdrop) */}
      <div className="hidden lg:flex items-center flex-1 max-w-md xl:max-w-xl mx-4 relative" ref={searchContainerRef}>
        <div
          className={`w-full flex items-center justify-between px-3.5 py-1.5 border transition-all duration-150 shadow-2xs rounded-full bg-white dark:bg-[#0d1117] ${
            showSearch
              ? "border-blue-600 ring-1 ring-blue-600/30 dark:border-blue-500"
              : "border-slate-200 dark:border-[#30363d] hover:border-slate-300 dark:hover:border-[#484f58]"
          }`}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <svg className="w-3.5 h-3.5 text-slate-400 dark:text-[#848d97] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={handleQueryChange}
              onFocus={() => {
                updateSearchCoords();
                setShowSearch(true);
              }}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search projects, tasks, daily logs, vendors, bills..."
              className="w-full bg-transparent dark:bg-transparent text-xs text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-[#6e7681] border-none outline-none focus:outline-none focus:ring-0 focus:bg-transparent shadow-none"
              style={{ backgroundColor: 'transparent' }}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          {searchQuery ? (
            <button
              onClick={handleClearQuery}
              className="shrink-0 p-0.5 rounded-full hover:bg-slate-100 dark:hover:bg-[#21262d] text-slate-400 hover:text-slate-700 dark:hover:text-white transition"
              title="Clear"
            >
              <XMarkIcon className="w-3.5 h-3.5" />
            </button>
          ) : (
            <kbd className="font-mono bg-slate-100 dark:bg-[#161b22] border border-slate-200 dark:border-[#30363d] px-1.5 py-0.5 rounded text-[10px] text-slate-500 dark:text-[#848d97] shrink-0">Ctrl K</kbd>
          )}
        </div>

        {/* Desktop Attached Dropdown Sheet directly underneath rendered via Portal to guarantee it is NEVER behind anything */}
        {showSearch && createPortal(
          <div
            ref={searchListRef}
            style={{
              position: 'fixed',
              top: `${searchCoords.top}px`,
              left: `${searchCoords.left}px`,
              width: `${searchCoords.width}px`,
              zIndex: 99999999,
            }}
            className="bg-white dark:bg-[#161b22] border border-slate-200/90 dark:border-[#30363d] shadow-2xl rounded-2xl overflow-hidden max-h-96 overflow-y-auto animate-fade-in-up"
          >
            {renderSearchContent()}
          </div>,
          document.body
        )}
      </div>

      <div className="flex space-x-1.5 sm:space-x-2 md:space-x-2.5 items-center shrink-0">

        {/* ── MOBILE QUICK ACTIONS HUB TRIGGER (SM:HIDDEN) ── */}
        <div className="relative sm:hidden">
          <button
            ref={mobileHubButtonRef}
            onClick={toggleMobileHub}
            className={`h-9 px-2 rounded-xl border transition-all duration-200 shadow-2xs flex items-center gap-1.5 cursor-pointer active:scale-95 relative ${
              showMobileHub
                ? "bg-orange-50 dark:bg-orange-950/40 border-[#E8612C] text-[#E8612C]"
                : "bg-white dark:bg-[#161b22] border-slate-200/80 dark:border-[#30363d]/80 text-slate-700 dark:text-[#f0f6fc] hover:bg-slate-50 dark:hover:bg-[#21262d]"
            }`}
            title="Quick Menu & Profile"
            aria-label="Quick Menu"
          >
            {/* User Initial Avatar Badge */}
            <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-orange-400 to-[#E8612C] text-white font-bold text-xs flex items-center justify-center uppercase shadow-2xs shrink-0">
              {user?.name ? user.name.charAt(0) : "A"}
            </div>

            {/* Hub Chevron */}
            <svg
              className={`w-3.5 h-3.5 text-slate-400 dark:text-[#848d97] transition-transform duration-200 ${showMobileHub ? "rotate-180 text-[#E8612C]" : ""}`}
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
            </svg>

            {/* Notification Badge ping if unread */}
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-[#E8612C] border border-white dark:border-[#161b22]"></span>
              </span>
            )}
          </button>

          {/* ── MOBILE ACTIONS DROPDOWN (PORTAL) ── */}
          {showMobileHub && createPortal(
            <div
              ref={mobileHubRef}
              style={{
                position: "fixed",
                top: `${menuCoords.top}px`,
                right: `${menuCoords.right}px`,
                width: "calc(100vw - 24px)",
                maxWidth: "310px",
                zIndex: 999999,
              }}
              className="bg-white dark:bg-[#161b22] border border-slate-200/90 dark:border-[#30363d] shadow-2xl rounded-2xl overflow-hidden animate-fade-in-up"
            >
              {/* Header: User Profile Info */}
              <div className="p-3.5 border-b border-slate-100 dark:border-[#30363d] bg-gradient-to-r from-slate-50 via-white to-slate-50 dark:from-[#21262d] dark:via-[#161b22] dark:to-[#21262d] flex items-center justify-between">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-400 to-[#E8612C] text-white font-bold text-xs flex items-center justify-center uppercase shadow-2xs shrink-0">
                    {user?.name ? user.name.charAt(0) : "A"}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-slate-900 dark:text-white truncate">
                      {user?.name || "Admin User"}
                    </p>
                    <p className="text-[10px] text-slate-500 dark:text-[#848d97] truncate">
                      {user?.role || "Manager"} • {user?.email || "admin@briqerp.com"}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowMobileHub(false);
                    setShowProfilePopup(true);
                  }}
                  className="px-2 py-1 text-[11px] font-semibold text-[#E8612C] hover:text-orange-600 bg-orange-50 dark:bg-orange-950/40 rounded-lg border border-orange-200/60 dark:border-orange-900/60 transition shrink-0 cursor-pointer"
                >
                  Profile
                </button>
              </div>

              {/* Action Rows */}
              <div className="p-2 space-y-1">
                {/* 1. Search */}
                <button
                  onClick={() => {
                    setShowMobileHub(false);
                    setShowSearch(true);
                  }}
                  className="w-full flex items-center justify-between p-2 rounded-xl hover:bg-slate-50 dark:hover:bg-[#21262d] text-slate-700 dark:text-[#f0f6fc] transition group text-left cursor-pointer"
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-800 dark:text-white">Quick Search</p>
                      <p className="text-[10px] text-slate-400 dark:text-[#848d97]">Projects, tasks, logs</p>
                    </div>
                  </div>
                  <span className="text-[10px] text-slate-400 dark:text-[#848d97] bg-slate-100 dark:bg-[#30363d] px-1.5 py-0.5 rounded font-mono">
                    Search
                  </span>
                </button>

                {/* 2. Notifications */}
                <button
                  onClick={() => {
                    setShowMobileHub(false);
                    setMenuCoords(calcCoords(mobileHubButtonRef, 320));
                    setShowNotifications(true);
                  }}
                  className="w-full flex items-center justify-between p-2 rounded-xl hover:bg-slate-50 dark:hover:bg-[#21262d] text-slate-700 dark:text-[#f0f6fc] transition group text-left cursor-pointer"
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-orange-50 dark:bg-orange-950/50 text-[#E8612C] flex items-center justify-center shrink-0">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-800 dark:text-white">Notifications</p>
                      <p className="text-[10px] text-slate-400 dark:text-[#848d97]">
                        {unreadCount > 0 ? `${unreadCount} unread alert${unreadCount > 1 ? "s" : ""}` : "No new alerts"}
                      </p>
                    </div>
                  </div>
                  {unreadCount > 0 ? (
                    <span className="bg-[#E8612C] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-2xs">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  ) : (
                    <span className="text-[10px] text-slate-400 dark:text-[#848d97]">0</span>
                  )}
                </button>

                {/* 3. Theme Toggle */}
                <div className="flex items-center justify-between p-2 rounded-xl hover:bg-slate-50 dark:hover:bg-[#21262d] text-slate-700 dark:text-[#f0f6fc] transition">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-amber-50 dark:bg-amber-950/50 text-amber-500 flex items-center justify-center shrink-0">
                      {theme === "dark" ? <SunIcon className="w-4 h-4 text-amber-400" /> : <MoonIcon className="w-4 h-4 text-slate-600" />}
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-800 dark:text-white">Appearance</p>
                      <p className="text-[10px] text-slate-400 dark:text-[#848d97]">
                        {theme === "dark" ? "Dark Theme" : "Light Theme"}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      saveSettings({ theme: theme === "dark" ? "light" : "dark" });
                    }}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-[#30363d] hover:bg-slate-200 dark:hover:bg-[#484f58] text-slate-700 dark:text-[#f0f6fc] transition cursor-pointer border border-slate-200/60 dark:border-[#484f58]/60"
                  >
                    {theme === "dark" ? (
                      <>
                        <SunIcon className="w-3.5 h-3.5 text-amber-400" />
                        <span>Light</span>
                      </>
                    ) : (
                      <>
                        <MoonIcon className="w-3.5 h-3.5 text-slate-600" />
                        <span>Dark</span>
                      </>
                    )}
                  </button>
                </div>

                {/* 4. Settings */}
                <button
                  onClick={() => {
                    setShowMobileHub(false);
                    navigate("/admin/settings");
                  }}
                  className="w-full flex items-center justify-between p-2 rounded-xl hover:bg-slate-50 dark:hover:bg-[#21262d] text-slate-700 dark:text-[#f0f6fc] transition group text-left cursor-pointer"
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-800 dark:text-white">Settings</p>
                      <p className="text-[10px] text-slate-400 dark:text-[#848d97]">System preferences</p>
                    </div>
                  </div>
                  <svg className="w-3.5 h-3.5 text-slate-400 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              {/* Sign Out inside Dropdown */}
              <div className="p-2 border-t border-slate-100 dark:border-[#30363d] bg-slate-50/50 dark:bg-[#21262d]/50">
                <button
                  onClick={() => {
                    setShowMobileHub(false);
                    setShowLogoutConfirm(true);
                  }}
                  className="w-full flex items-center justify-center gap-2 py-1.5 px-3 text-xs font-bold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-xl transition cursor-pointer"
                >
                  <ArrowRightOnRectangleIcon className="w-4 h-4 shrink-0" />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>,
            document.body
          )}
        </div>

        {/* ── DESKTOP BUTTONS (HIDDEN ON MOBILE < SM) ── */}

        {/* 1. Desktop Theme Quick Toggle */}
        <button
          onClick={() => saveSettings({ theme: theme === 'dark' ? 'light' : 'dark' })}
          className="hidden sm:flex w-9 h-9 sm:w-10 sm:h-10 rounded-xl border border-slate-200/80 dark:border-[#30363d]/80 bg-white dark:bg-[#161b22] hover:bg-slate-50 dark:hover:bg-[#21262d] text-slate-600 dark:text-[#848d97] hover:text-slate-900 dark:hover:text-white transition-all shadow-2xs items-center justify-center shrink-0 cursor-pointer"
          title={theme === 'dark' ? "Switch to Light Mode" : "Switch to Dark Mode"}
          aria-label="Toggle Theme"
        >
          {theme === 'dark' ? (
            <SunIcon className="w-5 h-5 text-amber-400 hover:rotate-45 transition-transform" />
          ) : (
            <MoonIcon className="w-5 h-5 text-slate-600 hover:-rotate-12 transition-transform" />
          )}
        </button>

        {/* 2. Desktop/Tablet Search Button (visible only sm..lg when inline input is hidden) */}
        <div className="relative hidden sm:block lg:hidden">
          <button
            ref={searchButtonRef}
            onClick={toggleSearch}
            className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl border border-slate-200/80 dark:border-[#30363d]/80 bg-white dark:bg-[#161b22] hover:bg-slate-50 dark:hover:bg-[#21262d] text-slate-600 dark:text-[#848d97] hover:text-slate-900 dark:hover:text-white transition-all shadow-2xs flex items-center justify-center shrink-0 cursor-pointer ${showSearch ? "border-blue-600 text-blue-600 dark:border-blue-500 dark:text-blue-400" : ""}`}
            title="Search"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
          </button>

          {showSearch && createPortal(
            <div
              ref={mobileSearchRef}
              style={{ zIndex: 99999999 }}
              className="lg:hidden fixed top-16 left-3 right-3 sm:left-auto sm:right-6 sm:w-[420px] bg-white dark:bg-[#161b22] border border-slate-200 dark:border-[#30363d] shadow-2xl rounded-2xl overflow-hidden animate-fade-in-up"
            >
              <div className="flex items-center gap-2.5 px-3.5 py-2.5 border-b border-slate-100 dark:border-[#30363d] bg-white dark:bg-[#0d1117]">
                <svg className="w-4 h-4 text-slate-400 dark:text-[#848d97] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                </svg>
                <input
                  ref={mobileInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={handleQueryChange}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search projects, tasks, daily logs..."
                  className="flex-1 bg-transparent dark:bg-transparent text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-[#6e7681] border-none outline-none focus:outline-none focus:ring-0 focus:bg-transparent shadow-none"
                  style={{ backgroundColor: 'transparent' }}
                  autoComplete="off"
                  spellCheck={false}
                />
                {searchQuery ? (
                  <button onClick={handleClearQuery} className="p-1 text-slate-400 hover:text-slate-700 dark:hover:text-white">
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                ) : (
                  <button onClick={() => setShowSearch(false)} className="text-[10px] font-semibold text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white px-1.5 py-0.5 rounded border border-slate-200 dark:border-[#30363d]">
                    ESC
                  </button>
                )}
              </div>
              <div className="max-h-[60vh] overflow-y-auto">
                {renderSearchContent()}
              </div>
            </div>,
            document.body
          )}
        </div>

        {/* 3. Desktop Notifications */}
        <div className="relative hidden sm:block">
          <button
            ref={notificationButtonRef}
            onClick={toggleNotifications}
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl border border-slate-200/80 dark:border-[#30363d]/80 bg-white dark:bg-[#161b22] hover:bg-slate-50 dark:hover:bg-[#21262d] text-slate-600 dark:text-[#848d97] hover:text-slate-900 dark:hover:text-white transition-all shadow-2xs flex items-center justify-center relative shrink-0 cursor-pointer"
            title="Notifications"
          >
            <svg className="w-5 h-5 text-slate-600 dark:text-[#848d97]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
            </svg>
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#D9531E] text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center border-2 border-white dark:border-[#161b22] shadow-xs">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>

          {showNotifications && createPortal(
            <div
              ref={notificationRef}
              style={{ position: "fixed", top: `${menuCoords.top}px`, right: `${menuCoords.right}px`, left: window.innerWidth < 640 ? "8px" : "auto", zIndex: 999999 }}
              className="w-auto sm:w-80 md:w-[26rem] bg-white dark:bg-[#161b22] shadow-2xl rounded-2xl overflow-hidden animate-fade-in-up"
            >
              <div className="flex justify-between items-center px-4 py-3 border-b border-slate-100 dark:border-[#30363d] bg-slate-50/70 dark:bg-[#21262d]">
                <div>
                  <h3 className="font-bold text-slate-800 dark:text-[#f0f6fc] text-sm">Notifications</h3>
                  <p className="text-xs text-slate-500 dark:text-[#848d97] mt-0.5">You have {unreadCount} unread messages</p>
                </div>
                {unreadCount > 0 && (
                  <button onClick={markAllRead} className="text-xs font-semibold text-[#D9531E] hover:text-orange-700 transition shrink-0 ml-2">
                    Mark all read
                  </button>
                )}
              </div>
              <ul className="max-h-[55vh] sm:max-h-[60vh] md:max-h-[28rem] overflow-y-auto divide-y divide-slate-100 dark:divide-[#30363d] scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-[#30363d]">
                {notifications.length > 0 ? (
                  notifications.map((note) => (
                    <li
                      key={note.id}
                      onClick={() => handleNotificationClick(note)}
                      className={`px-4 py-3 flex gap-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-[#21262d] transition relative group ${!note.is_read ? "bg-orange-50/30 dark:bg-orange-950/20" : ""}`}
                    >
                      <div className="mt-1 flex-shrink-0">{getNotificationIcon(note.type)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start gap-2">
                          <p className={`text-sm ${!note.is_read ? "font-bold text-slate-900 dark:text-white" : "font-medium text-slate-700 dark:text-[#f0f6fc]"}`}>
                            {note.title}
                          </p>
                          <span className="text-[10px] text-slate-400 dark:text-[#6e7681] whitespace-nowrap shrink-0">
                            {new Date(note.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-[#848d97] mt-1 line-clamp-2 leading-relaxed">{note.message}</p>
                      </div>
                      {!note.is_read && (
                        <span className="absolute top-4 right-4 w-2 h-2 bg-[#D9531E] rounded-full shrink-0" />
                      )}
                    </li>
                  ))
                ) : (
                  <li className="px-5 py-10 text-center flex flex-col items-center">
                    <div className="bg-slate-50 dark:bg-[#21262d] p-4 rounded-full mb-3">
                      <InformationCircleIcon className="w-8 h-8 text-slate-400 dark:text-[#6e7681]" />
                    </div>
                    <p className="text-slate-500 dark:text-[#848d97] font-medium text-sm">No new notifications</p>
                  </li>
                )}
              </ul>
              <div className="bg-slate-50/70 dark:bg-[#21262d]/50 px-5 py-3 border-t border-slate-100 dark:border-[#30363d] text-center">
                <button
                  onClick={() => { navigate("/notifications"); setShowNotifications(false); }}
                  className="text-xs font-semibold text-[#D9531E] hover:text-orange-700 w-full"
                >
                  View All Notifications
                </button>
              </div>
            </div>,
            document.body
          )}
        </div>

        {/* 4. Desktop User Profile */}
        <div className="relative hidden sm:block">
          <button
            ref={profileButtonRef}
            onClick={toggleProfile}
            className="flex items-center gap-2 h-9 sm:h-10 px-2 sm:px-2.5 rounded-xl border border-slate-200/80 dark:border-[#30363d]/80 bg-white dark:bg-[#161b22] hover:bg-slate-50 dark:hover:bg-[#21262d] transition-all shadow-2xs cursor-pointer shrink-0"
            title="Profile menu"
          >
            <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-orange-50 dark:bg-orange-950/40 text-[#D9531E] font-bold text-xs flex items-center justify-center border border-orange-200/80 dark:border-orange-800/60 uppercase shrink-0">
              {user?.name ? user.name.charAt(0) : "A"}
            </div>
            <div className="text-left hidden md:block">
              <p className="text-xs font-semibold text-slate-800 dark:text-white leading-tight truncate max-w-[90px]">{user?.name || "Admin"}</p>
              <p className="text-[10px] text-slate-400 dark:text-[#848d97] capitalize leading-none">{user?.role || "Manager"}</p>
            </div>
          </button>

          {showProfile && createPortal(
            <div
              ref={profileRef}
              style={{ position: "fixed", top: `${menuCoords.top}px`, right: `${menuCoords.right}px`, left: window.innerWidth < 640 ? "auto" : "auto", zIndex: 999999 }}
              className="w-56 sm:w-60 bg-white dark:bg-[#161b22] shadow-2xl rounded-2xl overflow-hidden animate-fade-in-up"
            >
              <div className="px-4 py-3 border-b border-slate-100 dark:border-[#30363d] bg-slate-50/70 dark:bg-[#21262d]">
                <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{user?.name || "Admin User"}</p>
                <p className="text-xs text-slate-500 dark:text-[#848d97] truncate">{user?.email || "admin@example.com"}</p>
              </div>
              <button onClick={() => { setShowProfilePopup(true); setShowProfile(false); }} className="block w-full text-left px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-[#21262d] text-sm text-slate-700 dark:text-[#f0f6fc] transition font-medium">
                My Profile
              </button>
              <button onClick={() => navigate("/admin/settings")} className="block w-full text-left px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-[#21262d] text-sm text-slate-700 dark:text-[#f0f6fc] transition font-medium">
                Settings
              </button>
              <div className="border-t border-slate-100 dark:border-[#30363d] bg-red-50/20 dark:bg-red-950/20">
                <button
                  onClick={() => {
                    setShowProfile(false);
                    setShowLogoutConfirm(true);
                  }}
                  className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-red-600 dark:text-red-400 font-bold hover:bg-red-50 dark:hover:bg-red-950/40 transition"
                >
                  <ArrowRightOnRectangleIcon className="w-4 h-4 shrink-0" />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>,
            document.body
          )}
        </div>

        {/* ── STANDARDIZED SIGN OUT / LOG OUT BUTTON (VISIBLE ON BOTH MOBILE & DESKTOP) ── */}
        <button 
          onClick={() => setShowLogoutConfirm(true)} 
          className="group relative w-9 h-9 sm:w-10 sm:h-10 rounded-xl text-slate-600 dark:text-[#848d97] hover:text-rose-600 dark:hover:text-rose-400 bg-white dark:bg-[#161b22] hover:bg-rose-50/90 dark:hover:bg-rose-950/40 border border-slate-200/80 dark:border-[#30363d]/80 hover:border-rose-200 dark:hover:border-rose-900/60 transition-all duration-200 shadow-2xs hover:shadow-xs active:scale-95 flex items-center justify-center cursor-pointer shrink-0"
          title="Sign Out / Log Out"
          aria-label="Sign Out"
        >
          <ArrowRightOnRectangleIcon className="w-5 h-5 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" />
        </button>
      </div>

      {/* ── PROFILE MODAL (REAL USER DATA) ── */}
      {showProfilePopup && createPortal(
        <div 
          onClick={(e) => { if (e.target === e.currentTarget) setShowProfilePopup(false); }}
          className="fixed inset-0 bg-slate-900/60 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-[999999] p-4 animate-fade-in"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white dark:bg-[#161b22] w-full max-w-lg rounded-3xl shadow-2xl border border-slate-200/80 dark:border-[#30363d] overflow-hidden animate-fade-in-up">
            
            {/* Top Brand Banner */}
            <div className="h-24 sm:h-28 bg-gradient-to-r from-[#1B2A41] via-[#243B5A] to-[#1B2A41] relative px-6 flex items-center justify-end overflow-hidden">
              {/* Subtle brand ambient glow */}
              <div className="absolute -top-12 -left-12 w-44 h-44 bg-[#E8612C]/25 rounded-full blur-2xl pointer-events-none" />
              <div className="absolute -bottom-12 -right-12 w-44 h-44 bg-blue-500/20 rounded-full blur-2xl pointer-events-none" />

              <button 
                onClick={() => setShowProfilePopup(false)} 
                className="relative z-10 text-slate-300 hover:text-white hover:bg-white/10 rounded-xl p-1.5 transition"
                title="Close"
              >
                <XMarkIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            {/* Profile Info Content */}
            <div className="px-5 sm:px-8 pb-6 sm:pb-8 relative">
              {/* Avatar & Status Row */}
              <div className="-mt-12 sm:-mt-14 mb-4 flex justify-between items-end">
                {user?.avatar ? (
                  <img 
                    src={user.avatar} 
                    alt={user.name || "User"} 
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover border-4 border-white dark:border-[#161b22] shadow-xl bg-white dark:bg-[#21262d]" 
                  />
                ) : (
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-gradient-to-br from-[#1B2A41] to-[#2E4057] text-[#E8612C] font-black text-2xl sm:text-3xl flex items-center justify-center border-4 border-white dark:border-[#161b22] shadow-xl ring-1 ring-black/5 dark:ring-white/10">
                    {getUserInitials()}
                  </div>
                )}

                <div className="mb-1">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                    user?.status === "inactive" || user?.status === "Inactive"
                      ? "bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-800/60"
                      : "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/60 shadow-2xs"
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      user?.status === "inactive" || user?.status === "Inactive" ? "bg-rose-500" : "bg-emerald-500 animate-pulse"
                    }`} />
                    {user?.status ? (user.status.charAt(0).toUpperCase() + user.status.slice(1).toLowerCase()) : "Active"}
                  </span>
                </div>
              </div>

              {/* Name & Contact */}
              <div className="mb-5">
                <h2 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white leading-tight">
                  {user?.name || "User Account"}
                </h2>
                <div className="flex flex-wrap items-center gap-y-1 gap-x-3 mt-1 text-xs text-slate-500 dark:text-[#848d97]">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                    <span className="font-mono text-slate-600 dark:text-slate-300 font-medium">{user?.email || "user@example.com"}</span>
                  </span>
                  {user?.phone && (
                    <span className="text-slate-400 dark:text-slate-500 font-mono">
                      • {user.phone}
                    </span>
                  )}
                </div>
              </div>

              {/* Real Data Meta Grid */}
              <div className="grid grid-cols-2 gap-2.5 sm:gap-3 bg-slate-50 dark:bg-[#21262d] p-3.5 sm:p-4 rounded-2xl border border-slate-200/70 dark:border-[#30363d] mb-6">
                <div className="p-2">
                  <p className="text-[11px] text-slate-400 dark:text-[#848d97] uppercase font-bold tracking-wider">System Role</p>
                  <p className="text-xs sm:text-sm font-bold text-[#E8612C] dark:text-[#FB923C] mt-0.5">
                    {user?.role || "Team Member"}
                  </p>
                </div>

                <div className="p-2">
                  <p className="text-[11px] text-slate-400 dark:text-[#848d97] uppercase font-bold tracking-wider">Username</p>
                  <p className="text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-200 mt-0.5 font-mono truncate">
                    {getUserHandle()}
                  </p>
                </div>

                <div className="p-2">
                  <p className="text-[11px] text-slate-400 dark:text-[#848d97] uppercase font-bold tracking-wider">Organization</p>
                  <p className="text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-200 mt-0.5 truncate">
                    {user?.company_name || user?.company || (user?.role === 'Customer' ? (user?.name || 'Client Account') : 'BriqERP Internal')}
                  </p>
                </div>

                <div className="p-2">
                  <p className="text-[11px] text-slate-400 dark:text-[#848d97] uppercase font-bold tracking-wider">Member Since</p>
                  <p className="text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-200 mt-0.5">
                    {getJoinedDate()}
                  </p>
                </div>

                <div className="col-span-2 p-2 pt-2 border-t border-slate-200/60 dark:border-[#30363d]/80 flex items-center justify-between text-xs">
                  <span className="text-[11px] text-slate-400 dark:text-[#848d97] uppercase font-bold tracking-wider">Session Status</span>
                  <span className="font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    {getLastLoginDisplay()}
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button 
                  onClick={() => {
                    setShowProfilePopup(false);
                    setShowLogoutConfirm(true);
                  }} 
                  className="flex-1 bg-white dark:bg-[#161b22] border border-rose-200 dark:border-rose-900/50 text-rose-600 dark:text-rose-400 py-2.5 rounded-xl font-bold hover:bg-rose-50 dark:hover:bg-rose-950/40 transition shadow-2xs text-xs sm:text-sm flex items-center justify-center gap-2"
                >
                  <ArrowRightOnRectangleIcon className="w-4 h-4 shrink-0" />
                  <span>Sign Out</span>
                </button>
                <button 
                  onClick={() => setShowProfilePopup(false)} 
                  className="flex-1 bg-[#1B2A41] hover:bg-[#121E30] dark:bg-[#21262d] dark:hover:bg-[#30363d] text-white py-2.5 rounded-xl font-bold transition shadow-sm text-xs sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── PREMIUM SIGN OUT / LOG OUT MODAL ── */}
      {showLogoutConfirm && createPortal(
        <div 
          onClick={(e) => { if (e.target === e.currentTarget) setShowLogoutConfirm(false); }}
          className="fixed inset-0 bg-slate-900/60 dark:bg-black/75 backdrop-blur-xs flex items-center justify-center z-[999999] p-4 animate-fade-in"
          role="dialog"
          aria-modal="true"
          aria-labelledby="logout-dialog-title"
        >
          <div className="relative w-full max-w-sm sm:max-w-md bg-white dark:bg-[#161b22] rounded-2xl shadow-2xl border border-slate-200/80 dark:border-[#30363d] overflow-hidden animate-fade-in-up">
            {/* Close button */}
            <button 
              onClick={() => setShowLogoutConfirm(false)}
              className="absolute top-3.5 right-3.5 p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-[#f0f6fc] hover:bg-slate-100 dark:hover:bg-[#21262d] rounded-xl transition-colors"
              title="Close"
              aria-label="Close dialog"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>

            <div className="p-5 sm:p-6 text-center">
              {/* Premium Icon Badge */}
              <div className="mx-auto w-14 h-14 rounded-2xl bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 flex items-center justify-center border border-rose-100 dark:border-rose-900/40 shadow-inner mb-4 ring-8 ring-rose-50/50 dark:ring-rose-950/20">
                <ArrowRightOnRectangleIcon className="w-7 h-7" />
              </div>

              {/* Title & Description */}
              <h2 id="logout-dialog-title" className="text-base sm:text-lg font-bold text-slate-900 dark:text-white tracking-tight">
                Sign Out of BriqERP
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 dark:text-[#848d97] mt-1.5 max-w-xs mx-auto leading-relaxed">
                Are you sure you want to end your session? You will be safely logged out and redirected to the sign-in page.
              </p>

              {/* Current Account Session Info Card */}
              <div className="mt-5 p-3.5 bg-slate-50 dark:bg-[#0d1117]/80 rounded-xl border border-slate-200/70 dark:border-[#30363d] text-left">
                <div className="flex items-center gap-3">
                  <div className="relative shrink-0">
                    {user?.avatar ? (
                      <img 
                        src={user.avatar} 
                        alt={user?.name || "User"} 
                        className="w-10 h-10 rounded-xl object-cover border border-slate-200 dark:border-[#30363d]" 
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D9531E] to-amber-600 text-white font-bold text-sm flex items-center justify-center shadow-xs uppercase">
                        {user?.name ? user.name.charAt(0) : "A"}
                      </div>
                    )}
                    <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 border-2 border-white dark:border-[#0d1117] rounded-full" title="Active session" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                        {user?.name || "Admin User"}
                      </p>
                      <span className="px-2 py-0.5 text-[10px] font-semibold bg-orange-100/80 dark:bg-orange-950/50 text-[#D9531E] dark:text-orange-400 border border-orange-200/60 dark:border-orange-800/40 rounded-full shrink-0">
                        {user?.role || "Administrator"}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-[#848d97] truncate mt-0.5">
                      {user?.email || "admin@briqerp.com"}
                    </p>
                  </div>
                </div>

                {/* Subtext info */}
                <div className="mt-3 pt-2.5 border-t border-slate-200/60 dark:border-[#30363d]/80 flex items-center gap-2 text-[11px] text-slate-500 dark:text-[#848d97]">
                  <InformationCircleIcon className="w-4 h-4 text-slate-400 dark:text-[#6e7681] shrink-0" />
                  <span>Unsaved changes in active forms will be discarded.</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex items-center gap-3">
                <button 
                  type="button"
                  onClick={() => setShowLogoutConfirm(false)} 
                  className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200/80 dark:bg-[#21262d] dark:hover:bg-[#30363d] text-slate-700 dark:text-[#f0f6fc] text-xs sm:text-sm font-semibold rounded-xl border border-slate-200/60 dark:border-[#30363d] transition-colors shadow-2xs cursor-pointer"
                >
                  Cancel
                </button>
                <button 
                  type="button"
                  onClick={() => {
                    setShowLogoutConfirm(false);
                    handleLogout();
                  }} 
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-red-600 via-rose-600 to-red-600 hover:from-red-700 hover:via-rose-700 hover:to-red-700 text-white text-xs sm:text-sm font-semibold rounded-xl shadow-md shadow-rose-600/20 hover:shadow-lg hover:shadow-rose-600/30 transition-all duration-200 flex items-center justify-center gap-2 active:scale-98 cursor-pointer"
                >
                  <ArrowRightOnRectangleIcon className="w-4 h-4 shrink-0" />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Topbar;